import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { Modal, Button, Spinner } from 'react-bootstrap';
import Select from 'react-select';

const SignUpLayer = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    mobile: '',
    businessCard: '', // Will hold the Base64 string
    developer_id: '',
    type: 'developerSales',
  });

  // Changed error initial state to an array for multiple error messages
  const [error, setError] = useState([]);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [otp, setOtp] = useState('');
  const [otpTimer, setOtpTimer] = useState(60); // Timer in seconds
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [businessCardFileName, setBusinessCardFileName] = useState('');
  const navigate = useNavigate();

  const [developers, setDevelopers] = useState([]);

  // Transform developers data for react-select
  const developerOptions = useMemo(() => {
    return developers.map(dev => ({
      value: dev.id,
      label: dev.name
    }));
  }, [developers]);

  // Handle select change
  const handleSelectChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      developer_id: selectedOption ? selectedOption.value : ''
    }));
  };

  // Helper function to determine if field has error
  const hasFieldError = (fieldName) => {
    return validationErrors.some(error => 
      error.toLowerCase().includes(fieldName.toLowerCase())
    );
  };

  // Field Validations - Enhanced version
  const validateFields = () => {
    const errors = [];
    
    // Name validation
    if (!formData.name.trim()) {
      errors.push('Full Name is required');
    } else if (formData.name.trim().length < 2) {
      errors.push('Full Name must be at least 2 characters long');
    }
    
    // Business Email Validation: must be a business email (not gmail, yahoo, outlook, hotmail)
    if (!formData.email.trim()) {
      errors.push('Business Email is required');
    } else {
      const emailParts = formData.email.split('@');
      if (emailParts.length !== 2) {
        errors.push('Please enter a valid email address');
      } else {
        // Email format validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          errors.push('Please enter a valid email address');
        } else {
          const domain = emailParts[1].toLowerCase();
          const disallowedDomains = ['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'brokerz.com'];
          if (disallowedDomains.includes(domain)) {
            errors.push('Please use a business email address. Personal email domains are not allowed');
          }
        }
      }
    }
    
    // Mobile number validation
    if (!formData.mobile.trim()) {
      errors.push('Mobile Phone is required');
    } else {
      // Mobile number should be exactly 11 digits
      const phoneRegex = /^\d{11}$/;
      if (!phoneRegex.test(formData.mobile.trim())) {
        errors.push('Mobile number must be exactly 11 digits');
      }
    }
    
    // Password validation
    if (!formData.password) {
      errors.push('Password is required');
    } else {
      // Password Validation using regex
      const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&_])[A-Za-z\d@$!%*?&_]{8,}$/;
      if (!passwordRegex.test(formData.password)) {
        errors.push('Password must contain at least one uppercase letter, one number, one special character, and minimum 8 characters (@$!%*?&_)');
      }
    }
    
    // Password Confirmation
    if (!formData.password_confirmation) {
      errors.push('Password confirmation is required');
    } else if (formData.password !== formData.password_confirmation) {
      errors.push('Passwords do not match');
    }
    
    // Developer selection validation
    if (!formData.developer_id) {
      errors.push('Please select a developer');
    }
    
    // Business Card Validation: should be a valid image (Base64 string starting with "data:image/")
    if (!formData.businessCard || !formData.businessCard.startsWith('data:image/')) {
      errors.push('Please upload a valid business card image');
    }
    
    return errors;
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Clear validation errors when user starts typing
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
    
    setFormData({ ...formData, [name]: value });
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    if (field === 'password') {
      setShowPassword(!showPassword);
    } else if (field === 'password_confirmation') {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  // Handle file input and convert to Base64
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const maxSize = 2 * 1024 * 1024; // 2MB

    if (file && file.size > maxSize) {
      setError(['File size exceeds the maximum limit of 2MB.']);
      e.target.value = null; // Reset the file input
      setBusinessCardFileName('');
    } else if (file) {
      setError([]); // Clear previous errors
      setBusinessCardFileName(file.name); // Store filename for display
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, businessCard: reader.result }); // Save as Base64
      };
      reader.readAsDataURL(file);
    } else {
      // File was cleared
      setBusinessCardFileName('');
      setFormData({ ...formData, businessCard: '' });
    }
  };

  useEffect(() => {
    fetchDevelopers();
  }, []);

  const fetchDevelopers = async () => {
    try {
      const endpoint = '/developers/approvedOnly';
      const { data } = await api.get(endpoint);
      if (data.status) {
        setDevelopers(data.response.data);
      } else {
        console.error('Error fetching developers:', data.message);
      }
    } catch (error) {
      console.error('Error fetching developers:', error);
    }
  };

  // Handle Signup Submission
  const handleSignUp = async (e) => {
    e.preventDefault();
    
    // Perform comprehensive validation
    const validationErrors = validateFields();
    if (validationErrors.length > 0) {
      setValidationErrors(validationErrors);
      setShowValidationModal(true);
      return;
    }
    
    setLoading(true);
    setError([]); // Clear previous errors

    try {
      // Instead of registering immediately, send OTP to email
      await api.post('/otp/sendOtp', { email: formData.email });
      setOtpTimer(60); // Reset OTP timer
      setShowOtpModal(true); // Show OTP modal
    } catch (err) {
      const errorResponse = err.response?.data?.response;
      let errorMessage = [];
      if (errorResponse && typeof errorResponse === 'object') {
        errorMessage = Object.values(errorResponse).flat();
      } else {
        errorMessage = ['An error occurred while sending OTP'];
      }
      setError(errorMessage);
    }
    setLoading(false);
  };

  // Handle OTP Verification and then call Register API
  const handleVerifyOtp = async () => {
    setLoading(true);
    try {
      const otpResponse = await api.post('/otp/verifyOtp', { email: formData.email, otp });
      if (otpResponse.data.status) {
        // Call register API after OTP is verified
        const regResponse = await api.post('/auth/register', formData);
        if (regResponse.data.status) {
          toast.success('Registered! Wait for your approval.');
          navigate('/sign-in');
        } else {
          setError(['Registration failed']);
        }
      } else {
        setError(['Invalid OTP']);
      }
    } catch (err) {
      setError(['Error during OTP verification or registration']);
    }
    setLoading(false);
  };

  // OTP Timer Effect
  useEffect(() => {
    let timerInterval;
    if (showOtpModal && otpTimer > 0) {
      timerInterval = setInterval(() => {
        setOtpTimer((prev) => (prev > 0 ? prev - 1 : prev));
      }, 1000);
    }
    return () => clearInterval(timerInterval);
  }, [showOtpModal, otpTimer]);

  // Handle Resend OTP
  const handleResendOtp = async () => {
    setLoading(true);
    try {
      await api.post('/otp/sendOtp', { email: formData.email });
      toast.success('OTP Resent Successfully!');
      setOtpTimer(60);
    } catch (error) {
      toast.error('Failed to resend OTP');
    }
    setLoading(false);
  };

  return (
    <>
      <section className="auth bg-base d-flex flex-wrap">
        <div className="auth-left d-lg-block d-none">
          <div className="d-flex align-items-center flex-column h-100 justify-content-center">
            <img
              src="assets/images/auth/1.jpg"
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </div>
        </div>
        <div className="auth-right py-32 px-24 d-flex flex-column justify-content-center">
          <div className="max-w-464-px mx-auto w-100">
            <Link to="/" className="mb-40 max-w-290-px">
              {/* <img src="assets/images/logo.png" alt="" /> */}
              <h3>Brokerz</h3>
            </Link>
            <h6 className="mb-12">Sign Up to your Account</h6>
            {error && error.length > 0 && (
              <ul className="text-danger mb-16">
                {error.map((msg, index) => (
                  <li key={index}>{msg}</li>
                ))}
              </ul>
            )}

            <form onSubmit={handleSignUp}>
              <div className="icon-field mb-16">
                <span className="icon top-50 translate-middle-y">
                  <Icon icon="f7:person" />
                </span>
                <input
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`form-control h-56-px bg-neutral-50 radius-12 ${hasFieldError('full name') ? 'border-danger' : ''}`}
                  required
                />
              </div>
              <div className="icon-field mb-16">
                <span className="icon top-50 translate-middle-y">
                  <Icon icon="mage:email" />
                </span>
                <input
                  type="email"
                  name="email"
                  placeholder="Business Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`form-control h-56-px bg-neutral-50 radius-12 ${hasFieldError('email') ? 'border-danger' : ''}`}
                  required
                />
              </div>
              <div className="icon-field mb-16">
                <span className="icon top-50 translate-middle-y">
                  <Icon icon="mage:phone" />
                </span>
                <input
                  type="tel"
                  name="mobile"
                  placeholder="Mobile Phone"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  className={`form-control h-56-px bg-neutral-50 radius-12 ${hasFieldError('mobile') ? 'border-danger' : ''}`}
                  pattern="[0-9]{11}"
                  required
                />
              </div>
              <span className="text-sm text-secondary-light fst-italic">
                Password should contain at least one uppercase, one number, and one special character and minimum 8 characters (@$!%*?&_).
              </span>
              <div className="icon-field mb-16 position-relative">
                <span className="icon top-50 translate-middle-y">
                  <Icon icon="solar:lock-password-outline" />
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`form-control h-56-px bg-neutral-50 radius-12 pr-50 ${hasFieldError('password') && !hasFieldError('confirmation') ? 'border-danger' : ''}`}
                  required
                />
                <span
                  className="eye-icon position-absolute end-0 top-50 translate-middle-y me-3 cursor-pointer"
                  onClick={() => togglePasswordVisibility('password')}
                >
                  <Icon icon={showPassword ? 'mdi:eye-off' : 'mdi:eye'} />
                </span>
              </div>

              <div className="icon-field mb-16 position-relative">
                <span className="icon top-50 translate-middle-y">
                  <Icon icon="solar:lock-password-outline" />
                </span>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="password_confirmation"
                  placeholder="Confirm Password"
                  value={formData.password_confirmation}
                  onChange={handleInputChange}
                  className={`form-control h-56-px bg-neutral-50 radius-12 pr-50 ${hasFieldError('confirmation') || hasFieldError('match') ? 'border-danger' : ''}`}
                  required
                />
                <span
                  className="eye-icon position-absolute end-0 top-50 translate-middle-y me-3 cursor-pointer"
                  onClick={() => togglePasswordVisibility('password_confirmation')}
                >
                  <Icon icon={showConfirmPassword ? 'mdi:eye-off' : 'mdi:eye'} />
                </span>
              </div>

              <div className="mb-20">
                <label htmlFor="developer_id" className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Developer <span className="text-danger-600">*</span>
                </label>
                <Select
                  inputId="developer_id"
                  name="developer_id"
                  options={developerOptions}
                  value={developerOptions.find(option => option.value === formData.developer_id)}
                  onChange={handleSelectChange}
                  placeholder="Search or select a developer..."
                  isClearable
                  required
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: hasFieldError('developer') ? '#dc3545' : (state.isFocused ? '#86b7fe' : '#dee2e6'),
                      boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
                      borderRadius: '0.5rem',
                      minHeight: '38px',
                    }),
                    input: (baseStyles) => ({ ...baseStyles, margin: '0px', padding: '0px' }),
                    valueContainer: (baseStyles) => ({ ...baseStyles, padding: '0px 8px' }),
                    placeholder: (baseStyles) => ({ ...baseStyles, color: '#6c757d' }),
                  }}
                />
              </div>
              <div className="icon-field mb-16">
                <div className="d-flex align-items-center mb-12">
                  <div className="bg-neutral-100 border rounded me-12" style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {formData.businessCard ? (
                      <img 
                        src={formData.businessCard} 
                        alt="Business Card Preview" 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                      />
                    ) : (
                      <Icon icon="mdi:card-account-details" className="text-neutral-400" style={{ fontSize: '24px' }} />
                    )}
                  </div>
                  <div>
                    <small className="text-neutral-700 fw-medium d-block">
                      {businessCardFileName ? `Selected: ${businessCardFileName}` : 'Business Card Upload'}
                    </small>
                    <small className="text-neutral-500">Upload your business card (PNG, JPG) max 2MB</small>
                  </div>
                </div>
                <input
                  type="file"
                  accept=".png, .jpg, .jpeg, image/png, image/jpeg, image/jpg"
                  onChange={handleFileChange}
                  className={`form-control h-56-px bg-neutral-50 radius-12 ${hasFieldError('business card') ? 'border-danger' : ''}`}
                  required
                />
                {formData.businessCard && (
                  <small className="text-success-600 d-block mt-2">
                    ✅ Business card uploaded successfully
                  </small>
                )}
              </div>
              <button
                type="submit"
                className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 mt-32 d-flex align-items-center justify-content-center"
                disabled={loading}
                style={{ minHeight: '56px' }}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Sending OTP...
                  </>
                ) : (
                  <>
                    <Icon icon="material-symbols:person-add" className="me-2" />
                    Sign Up
                  </>
                )}
              </button>
              <div className="mt-32 text-center text-sm">
                <p className="mb-0">
                  Already have an account?{" "}
                  <Link to="/sign-in" className="text-primary-600 fw-semibold">
                    Sign in
                  </Link>
                </p>
                <p className="mt-16 mb-0">
                  <Link to="/" className="text-secondary-light fw-medium">
                    <Icon icon="mdi:arrow-left" className="me-1" />
                    Back to Home
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* OTP Modal using react-bootstrap */}
      <Modal
        show={showOtpModal}
        onHide={() => setShowOtpModal(false)}
        centered
        backdrop="static"
        className="transparent-modal" // Use this class to add custom transparent styles if desired
      >
        <Modal.Header closeButton>
          <Modal.Title>Verify your Email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-secondary-light text-sm mb-16">
            An OTP has been sent to your email. Enter it below:
          </p>
          <input
            type="text"
            placeholder="Enter OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            className="form-control h-56-px bg-neutral-50 radius-12 mb-16"
          />
          <div className="d-flex justify-content-between align-items-center">
            {otpTimer > 0 ? (
              <span>Resend OTP in {otpTimer} seconds</span>
            ) : (
              <button onClick={handleResendOtp} className="btn btn-link" disabled={loading}>
                {loading ? <Spinner animation="border" size="sm" /> : 'Resend OTP'}
              </button>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleVerifyOtp} variant="primary" disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : 'Verify OTP'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Validation Error Modal */}
      <Modal 
        show={showValidationModal} 
        onHide={() => setShowValidationModal(false)}
        centered
        className="validation-modal"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="text-danger-600 d-flex align-items-center">
            <Icon icon="material-symbols:warning" className="me-2" style={{ fontSize: '24px' }} />
            Please Complete Required Fields
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          <p className="mb-3 text-neutral-700">
            Please fix the following issues before signing up:
          </p>
          <ul className="text-danger-600 mb-0 ps-3">
            {validationErrors.map((error, index) => (
              <li key={index} className="mb-2">{error}</li>
            ))}
          </ul>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-2">
          <Button 
            variant="primary" 
            onClick={() => setShowValidationModal(false)}
            className="px-4"
          >
            Got it, I'll fix these
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Responsive modal styles */}
      <style>{`
        .validation-modal .modal-dialog {
          margin: 1rem;
        }
        
        @media (max-width: 576px) {
          .validation-modal .modal-dialog {
            margin: 0.5rem;
          }
          
          .validation-modal .modal-body {
            padding: 1rem;
          }
          
          .validation-modal .modal-header {
            padding: 1rem 1rem 0.5rem 1rem;
          }
          
          .validation-modal .modal-footer {
            padding: 0.5rem 1rem 1rem 1rem;
          }
        }
      `}</style>
    </>
  );
};

export default SignUpLayer;
